import type { Card } from '@/types/entities'

/**
 * Late is a fact about dates, not a stage.
 *
 * The board once had a Backlog column that looked like a sixth stage, and a scheduled job that
 * rewrote overdue work to To do so it would land there. That destroyed the real stage — an
 * item overdue while awaiting sign-off stopped being awaiting sign-off, and its approvers
 * could no longer approve it.
 *
 * So lateness is derived, and it is derived TWICE, because there are two different facts:
 *
 * - `overdue` — not finished, and the target end date has passed. Still true of it right now.
 * - `late` — finished, but finished after the date. A closed fact about the past, and the one
 *   Insights needs: work delivered late still counts as delivered.
 *
 * Neither replaces `status`. The stage says where the work is; this says whether it is behind.
 */
export type Lateness = 'none' | 'overdue' | 'late'

export function latenessOf(
  card: Pick<Card, 'status' | 'dueAt' | 'completedAt' | 'inReviewAt'>,
  now: Date = new Date(),
): Lateness {
  if (!card.dueAt) return 'none'

  // Approval pending is never shown as currently overdue on the Work UI. At that point the
  // assignee has handed the item to an approver; whether it ARRIVED there on time is a
  // separate SLA fact handled by Insights. An on-time handoff also keeps eventual completion
  // from being labelled late after the approver takes their time.
  if (card.status === 'in_review') return 'none'
  if (card.inReviewAt && card.inReviewAt <= card.dueAt) return 'none'

  if (card.status === 'done') {
    // No completion date on a completed item is a broken row, not an on-time one — but it is
    // not this function's job to guess, and claiming "late" would be inventing a fact.
    return card.completedAt && card.completedAt > card.dueAt ? 'late' : 'none'
  }

  return card.dueAt < now.toISOString() ? 'overdue' : 'none'
}

/** What to say about it, in the words somebody reading a board would use. */
export function latenessLabel(l: Lateness): string | null {
  if (l === 'overdue') return 'Past its target end date'
  if (l === 'late') return 'Completed after its target end date'
  return null
}
